import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SubtopicIcon } from "../assets/imageAssets";
import { profilesIdTopics_req, userActivity_req } from "../api/profiles.api";
import { setTopicsTree } from "../redux/topics.slice";
import Product from "../components/Product";
import ProductSkeleton from "../components/ProductSkeleton";
import Questions from "./questions";
import ProfileUnavailable from "./profile-unavailable";

const findNode = (topics, id) => {
  for (const topic of (topics || [])) {
    if (topic.id === id) return topic;
    const found = findNode(topic.topics, id);
    if (found) return found;
  }
  return null;
};

const Subtopics = ({ seconds }) => {
  const { profileId, topicId, "*": rest } = useParams();
  const location = useLocation();
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const sessionId = useSelector((state) => state.me.sessionData);

  const [localTopicsTree, setLocalTopicsTree] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const segments = useMemo(() => (rest || "").split("/").filter(Boolean), [rest]);
  const isQuestionsView = segments.length > 0 && segments[segments.length - 1] === "questions";
  const subtopicIds = useMemo(
    () => segments.filter(s => s !== "subtopics" && s !== "questions").map(Number),
    [segments]
  );
  const leafId = subtopicIds.length > 0 ? subtopicIds[subtopicIds.length - 1] : Number(topicId);

  const currentNode = useMemo(() => {
    if (!localTopicsTree) return null;
    return findNode(localTopicsTree, leafId);
  }, [localTopicsTree, leafId]);

  const fetchTree = async () => {
    try {
      const response = await profilesIdTopics_req(profileId);
      const profile = response?.result?.profile;
      const topics = response?.result?.topics || [];
      setProfileData(profile);
      setLocalTopicsTree(topics);
      dispatch(setTopicsTree({
        profileId: Number(profileId),
        profileName: profile?.name,
        topicsTree: topics,
      }));
    } catch (error) {
      if (error?.response?.status === 404) {
        setProfileMissing(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setProfileMissing(false);
    setIsLoading(true);
    fetchTree();
  }, [profileId]);

  const handleTopicClick = async (itemId, item) => {
    await userActivity_req(sessionId, seconds, "work_pay_explorer");
    if (item?.is_question) {
      navigation(`${location.pathname}/subtopics/${itemId}/questions`);
    } else {
      navigation(`${location.pathname}/subtopics/${itemId}`);
    }
  };

  if (isLoading) {
    if (isQuestionsView) {
      return null;
    }
    return <ProductSkeleton levelIcon={SubtopicIcon} />;
  }

  if (profileMissing) {
    return <ProfileUnavailable />;
  }

  if (isQuestionsView) {
    return <Questions key={leafId} seconds={seconds} />;
  }

  return (
    <Product
      title={currentNode?.name}
      description={currentNode?.description}
      levelIcon={SubtopicIcon}
      pageColor={"purple"}
      topicsList={currentNode?.topics || []}
      onTopicClick={handleTopicClick}
      showBreadcrumbs={true}
    />
  );
};

export default Subtopics;
